import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { skillsApi } from '../../services/api'
import { showSuccess, showError } from './appSlice'

const initialState = {
  skills: [],
  skillCategories: [],
  isLoading: false,
  error: null,
}

// Async thunks
export const fetchSkills = createAsyncThunk(
  'skills/fetchSkills',
  async (_, { rejectWithValue }) => {
    try {
      const response = await skillsApi.getAll()
      
      if (response.success) {
        return response.data
      } else {
        throw new Error(response.error?.message || 'Ошибка загрузки навыков')
      }
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const createSkill = createAsyncThunk(
  'skills/createSkill',
  async (skillData, { dispatch, rejectWithValue }) => {
    try {
      const response = await skillsApi.create(skillData)
      
      if (response.success) {
        dispatch(showSuccess('Навык добавлен успешно'))
        dispatch(fetchSkills())
        return response.data
      } else {
        throw new Error(response.error?.message || 'Ошибка создания навыка')
      }
    } catch (error) {
      dispatch(showError(error.message || 'Ошибка при добавлении навыка'))
      return rejectWithValue(error.message)
    }
  }
)

export const updateSkill = createAsyncThunk(
  'skills/updateSkill',
  async ({ id, data }, { dispatch, rejectWithValue }) => {
    try {
      const response = await skillsApi.update(id, data)
      
      if (response.success) {
        dispatch(showSuccess('Навык обновлен успешно'))
        dispatch(fetchSkills())
        return response.data
      } else {
        throw new Error(response.error?.message || 'Ошибка обновления навыка')
      }
    } catch (error) {
      dispatch(showError(error.message || 'Ошибка при обновлении навыка'))
      return rejectWithValue(error.message)
    }
  }
)

export const deleteSkill = createAsyncThunk(
  'skills/deleteSkill',
  async (id, { dispatch, rejectWithValue }) => {
    try {
      const response = await skillsApi.delete(id)
      
      if (response.success) {
        dispatch(showSuccess('Навык удален успешно'))
        dispatch(fetchSkills())
        return id
      } else {
        throw new Error(response.error?.message || 'Ошибка удаления навыка')
      }
    } catch (error) {
      dispatch(showError(error.message || 'Ошибка при удалении навыка'))
      return rejectWithValue(error.message)
    }
  }
)

export const updateSkillOrder = createAsyncThunk(
  'skills/updateSkillOrder',
  async (skillsOrder, { dispatch, rejectWithValue }) => {
    try {
      const response = await skillsApi.updateOrder(skillsOrder)
      
      if (response.success) {
        dispatch(showSuccess('Порядок навыков обновлен'))
        return skillsOrder
      } else {
        throw new Error(response.error?.message || 'Ошибка обновления порядка')
      }
    } catch (error) {
      dispatch(showError(error.message || 'Ошибка при обновлении порядка'))
      return rejectWithValue(error.message)
    }
  }
)

const skillsSlice = createSlice({
  name: 'skills',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    
    reorderSkills: (state, action) => {
      state.skills = action.payload
    },
  },
  extraReducers: (builder) => {
    // Fetch Skills
    builder
      .addCase(fetchSkills.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchSkills.fulfilled, (state, action) => {
        // console.log(action);
        // console.log(state);
        state.isLoading = false
        state.skills = action.payload || []
        state.skillCategories = action.payload.categories || []
        state.error = null
      })
      .addCase(fetchSkills.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload
      })
    
    // Create Skill
    builder
      .addCase(createSkill.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(createSkill.fulfilled, (state) => {
        state.isLoading = false
        state.error = null
      })
      .addCase(createSkill.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload
      })
    
    // Update Skill
    builder
      .addCase(updateSkill.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(updateSkill.fulfilled, (state) => {
        state.isLoading = false
        state.error = null
      })
      .addCase(updateSkill.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload
      })
    
    // Delete Skill
    builder
      .addCase(deleteSkill.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(deleteSkill.fulfilled, (state) => {
        state.isLoading = false
        state.error = null
      })
      .addCase(deleteSkill.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload
      })
    
    // Update Skill Order
    builder
      .addCase(updateSkillOrder.fulfilled, (state, action) => {
        state.skills = action.payload
      })
  },
})

export const { clearError, reorderSkills } = skillsSlice.actions

// Selectors
export const selectSkillsByCategory = (state) => {
  const { skills } = state.skills
  const grouped = {}
  
  skills.forEach(skill => {
    const category = skill.category || 'Другое'
    if (!grouped[category]) {
      grouped[category] = []
    }
    grouped[category].push(skill)
  })
  
  // Sort skills within each category by order or level
  Object.keys(grouped).forEach(category => {
    grouped[category].sort((a, b) => {
      if (a.order !== undefined && b.order !== undefined) {
        return a.order - b.order
      }
      return b.level - a.level
    })
  })
  
  return grouped
}

export const selectTopSkills = (state, limit = 6) => {
  const { skills } = state.skills
  
  return skills
    .filter(skill => skill.level >= 80)
    .sort((a, b) => b.level - a.level)
    .slice(0, limit)
}

export default skillsSlice.reducer